import React, { useMemo } from 'react'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import LoadingSection from 'components_new/molecules/LoadingSection'
import SimpleTable from 'components_new/molecules/SimpleTable'

import DataHealthKpiCard from 'components_new/organisms/internal/DataHealth/KpiCard'
import DataHealthKpiGrid from 'components_new/organisms/internal/DataHealth/KpiGrid'
import DataHealthRerunKpiCard from 'components_new/organisms/internal/DataHealth/RerunKpiCard'
import DataHealthSectionCard from 'components_new/organisms/internal/DataHealth/SectionCard'

import { DataRerunSummary } from 'redux/selectors/internal/DataReruns'
import { DataRunsData } from 'redux/selectors/internal/DataRuns'

import {
  formatCostUsd,
  formatDayClock
} from 'components_new/organisms/internal/DataRuns/utils'

/** Rows in the card. Enough to see the shape of the morning at a glance. */
const MAX_ROWS = 8

const CUSTOMER_COLUMNS = [
  { title: 'Kund' },
  { title: 'Körning', width: 110 },
  { title: 'Filleveranser', width: 130 },
  { title: 'Senaste körning', width: 160 },
  { title: '', width: 24 }
]

/** How the card ranks a customer: run failures, then missed deliveries. */
interface AttentionRow {
  customerId: string | null
  customerName: string
  failCount: number
  missingDeliveries: number
  latestStartedAt: string | null
}

export interface DataHealthOverviewTabProps {
  data: DataRunsData | null
  dataFetching: boolean
  /** Sets the shared customer filter and jumps to the Jobb tab. */
  onSelectCustomer: (customerId: string | null) => void
  /** Jumps to the Körningar tab. */
  onSelectReruns: () => void
  /** The rerun in progress, else the latest one, null when none exists. */
  rerun: DataRerunSummary | null
  /** Label for the cards' right-hand meta when a customer is in scope. */
  selectedCustomerName: string | null
}

/**
 * The Översikt tab: the morning check. Answers "what failed last night" and
 * "which customers need a look" on one screen.
 *
 * Nothing here is fetched for this tab - everything is derived from the
 * data-runs request the Datahälsa route already makes, which is also why the
 * KPI rows on this tab and on Jobb overlap: same numbers, different
 * scope. Data quality lives on its own tab; this one is only about runs.
 */
const DataHealthOverviewTab = (props: DataHealthOverviewTabProps) => {
  const {
    data,
    dataFetching,
    onSelectCustomer,
    onSelectReruns,
    rerun,
    selectedCustomerName
  } = props

  const scopeMeta = selectedCustomerName ?? 'Alla kunder'

  /**
   * Per-customer run and delivery counts, derived the same way the customer
   * select's own fail counts are (see the DataRuns reducer) - the response
   * carries no per-customer aggregate.
   */
  const attentionRows = useMemo<AttentionRow[]>(() => {
    const byId = new Map<string, AttentionRow>()

    const ensure = (
      customerId: string | null,
      customerName: string
    ): AttentionRow => {
      const key = customerId ?? 'demo'
      const existing = byId.get(key)

      if (existing) {
        return existing
      }

      const created: AttentionRow = {
        customerId,
        customerName,
        failCount: 0,
        missingDeliveries: 0,
        latestStartedAt: null
      }

      byId.set(key, created)

      return created
    }

    ;(data?.jobs ?? []).forEach((job) => {
      if (!job.customer) {
        return
      }

      const row = ensure(job.customer.id, job.customer.name)

      if (job.status === 'fail' || job.status === 'timeout') {
        row.failCount += 1
      }

      if (!row.latestStartedAt || job.started_at > row.latestStartedAt) {
        row.latestStartedAt = job.started_at
      }
    })
    ;(data?.file_deliveries ?? []).forEach((delivery) => {
      if (!delivery.customer) {
        return
      }

      const row = ensure(delivery.customer.id, delivery.customer.name)

      if (delivery.status === 'missing' || delivery.status === 'stopped') {
        row.missingDeliveries += 1
      }
    })

    return Array.from(byId.values())
      .filter((row) => row.failCount > 0 || row.missingDeliveries > 0)
      .sort((a, b) => {
        // Run failures outrank missed deliveries: a job that did not finish
        // means every number below it is about yesterday's data.
        if (a.failCount !== b.failCount) {
          return b.failCount - a.failCount
        }

        return b.missingDeliveries - a.missingDeliveries
      })
  }, [data])

  /**
   * Age of the oldest missed delivery, which is what makes a missing file
   * worth acting on - one missed night is noise, three months is a dead feed.
   */
  const oldestOverdueDays = useMemo(() => {
    const overdueHours = (data?.file_deliveries ?? [])
      .filter(
        (delivery) =>
          delivery.status === 'missing' || delivery.status === 'stopped'
      )
      .map((delivery) => delivery.overdue_hours ?? 0)

    if (overdueHours.length === 0) {
      return null
    }

    return Math.floor(Math.max(...overdueHours) / 24)
  }, [data])

  const missingDeliveries =
    (data?.file_delivery_aggregates.missing_count ?? 0) +
    (data?.file_delivery_aggregates.stopped_count ?? 0)

  const failCount = data?.aggregates.fail_count ?? 0

  if (!data && dataFetching) {
    return <LoadingSection description={['Hämtar datahälsa']} sx={{ pt: 4 }} />
  }

  const attentionCount = attentionRows.length
  const pagedAttentionRows = attentionRows.slice(0, MAX_ROWS)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      <DataHealthKpiGrid>
        <DataHealthKpiCard
          fill={
            failCount > 0
              ? {
                  bg: 'error.transparent',
                  border: 'error.light',
                  text: 'error.text'
                }
              : undefined
          }
          label="Fel i senaste körningen"
          sub={
            failCount > 0
              ? `${data?.aggregates.affected_customers ?? 0} kunder · av ${
                data?.aggregates.job_count ?? 0
              } jobb`
              : 'allt gick igenom'
          }
          value={failCount}
        />
        <DataHealthKpiCard
          label="Uteblivna filleveranser"
          sub={
            oldestOverdueDays === null
              ? 'inga uteblivna leveranser'
              : `äldsta ${oldestOverdueDays} dygn sen`
          }
          value={missingDeliveries}
        />
        <DataHealthKpiCard
          label="Jobb i natt"
          sub={`${scopeMeta} · est. ${formatCostUsd(
            data?.aggregates.estimated_cost_usd ?? null
          )}`}
          value={data?.aggregates.job_count ?? 0}
        />
        <DataHealthRerunKpiCard onClick={onSelectReruns} rerun={rerun} />
      </DataHealthKpiGrid>

      <DataHealthSectionCard
        meta="Sorterat på körningsfel, sedan uteblivna leveranser"
        pill={{
          color: attentionCount > 0 ? 'error' : 'default',
          label: String(attentionCount)
        }}
        square={attentionCount > 0 ? 'error.main' : 'success.main'}
        title="Kunder som behöver uppmärksamhet"
      >
        {attentionCount === 0 ? (
          <EmptyPlaceholder title="Inga fel och inga uteblivna leveranser just nu." />
        ) : (
          <SimpleTable
            columns={CUSTOMER_COLUMNS}
            disableSorting
            rows={pagedAttentionRows.map((row) => ({
              id: row.customerId ?? 'demo',
              onClick: () => onSelectCustomer(row.customerId),
              cells: [
                <Text key="customer" sx={{ fontWeight: 600 }} variant="body2">
                  {row.customerName}
                </Text>,
                <Tag
                  color={row.failCount > 0 ? 'error' : 'success'}
                  key="run"
                  label={row.failCount > 0 ? `${row.failCount} fel` : 'Lyckad'}
                />,
                <Text
                  key="deliveries"
                  sx={{ color: 'text.secondary' }}
                  variant="body2"
                >
                  {row.missingDeliveries > 0
                    ? `${row.missingDeliveries} uteblev`
                    : '–'}
                </Text>,
                <Text
                  key="latest"
                  sx={{
                    color: 'text.secondary',
                    fontVariantNumeric: 'tabular-nums'
                  }}
                  variant="body2"
                >
                  {formatDayClock(row.latestStartedAt)}
                </Text>,
                <Icon
                  key="chevron"
                  fontSize="small"
                  name="ArrowForwardIos"
                  sx={{ color: 'text.disabled' }}
                />
              ]
            }))}
          />
        )}
        {attentionCount > MAX_ROWS ? (
          <Text sx={{ color: 'text.disabled', mt: 1 }} variant="caption">
            {`Visar ${MAX_ROWS} av ${attentionCount} kunder. Klicka en rad för kundens jobb.`}
          </Text>
        ) : (
          <Text sx={{ color: 'text.disabled', mt: 1 }} variant="caption">
            Klicka en rad för kundens jobb.
          </Text>
        )}
      </DataHealthSectionCard>
    </Box>
  )
}

DataHealthOverviewTab.displayName = 'DataHealthOverviewTab'
export default DataHealthOverviewTab
